import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useFacilities } from '../api/use-catalogue.js';
import { useSlots, type Slot, type SlotsState } from '../api/use-slots.js';
import { useCartContext } from '../api/cart-provider.js';
import { clinicTimes, placesLabel } from '../api/clinics.js';
import { CartCta } from '../components/cart-cta.js';
import { WaitNote } from '../components/wait-note.js';

/**
 * Pick a time.
 *
 * NO FILTER. The list is every hour that can be booked at the chosen place:
 * hours nobody has taken, plus groups that still have room. A closed one to one
 * and a full group are simply absent, so there is nothing to filter out.
 *
 * An open hour carries no label at all — it is not a one to one or a group
 * until somebody books it, and telling a parent an hour is "unclaimed" puts
 * internal state on their screen. It said "Open" until 25 September; ruled off
 * because the chevron already says the row can be tapped, and "Open" read as a
 * status the hour did not have. A row with places left still says how many.
 *
 * The facility selector remembers the last choice, so a family that only uses
 * one place never thinks about it.
 *
 * It opens on the first week with an hour in it, not on this week: ruled 30
 * October. An empty week is a screen to tap past. A week the person stepped to
 * with the arrows stays where they put it.
 */

const REMEMBERED_FACILITY = 'coachtools.facility';

/** The most /api/slots answers in one request. */
const LOOK_AHEAD_DAYS = 31;
const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

/**
 * `showCart` is false for an MPC coach booking on behalf of an athlete (stage
 * B): there is no cart, so no "Go to cart".
 */
export function BookingRoute({ showCart = true }: { showCart?: boolean } = {}) {
  const facilities = useFacilities();
  const [facilityId, setFacilityId] = useState<string | null>(remembered);
  // Null until somebody taps an arrow: the first open week decides until then.
  const [steppedTo, setSteppedTo] = useState<number | null>(null);
  const { state: cartState } = useCartContext();

  const cart = cartState.status === 'ready' ? cartState.cart : null;

  // A facility nobody has chosen yet: use the first one offered rather than
  // showing an empty calendar and making them work out why.
  const chosen =
    facilityId ??
    (facilities.status === 'ready' ? (facilities.value[0]?.facility_id ?? null) : null);

  const lookAhead = useMemo(lookAheadWindow, []);
  const { state: ahead } = useSlots(
    lookAhead.from,
    lookAhead.to,
    chosen,
    facilities.status !== 'loading',
  );
  const looking = steppedTo === null && ahead.status === 'loading';
  const weekOffset = steppedTo ?? firstOpenWeek(ahead);

  const { from, to } = useMemo(() => weekWindow(weekOffset), [weekOffset]);
  const { state } = useSlots(from, to, chosen, !looking);

  function step(by: number) {
    setSteppedTo(weekOffset + by);
  }

  function choose(next: string) {
    setFacilityId(next);
    setSteppedTo(null);
    try {
      window.localStorage.setItem(REMEMBERED_FACILITY, next);
    } catch {
      // Private browsing, or storage turned off. The calendar still works; it
      // just asks again next time.
    }
  }

  // A clinic is the family's to book; the coach's Book has no page for it.
  const listed = (state.status === 'ready' ? state.slots : []).filter((slot) => showCart || !slot.clinic_id);
  const byDay = groupByDay(listed);

  return (
    <>
      <div className="booking-head">
        <label className="field" htmlFor="facility">
          <span>Where</span>
          <select
            id="facility"
            value={chosen ?? ''}
            onChange={(event) => choose(event.target.value)}
          >
            {facilities.status === 'ready' ? (
              facilities.value.map((facility) => (
                <option key={facility.facility_id} value={facility.facility_id}>
                  {facility.name}
                </option>
              ))
            ) : (
              <option value="">Loading…</option>
            )}
          </select>
        </label>

        <div className="week-step">
          <button
            type="button"
            aria-label="Previous week"
            onClick={() => step(-1)}
            disabled={weekOffset === 0 || looking}
          >
            ‹
          </button>
          <span>{looking ? '…' : weekLabel(from, to)}</span>
          <button type="button" aria-label="Next week" onClick={() => step(1)} disabled={looking}>
            ›
          </button>
        </div>
      </div>

      {state.status === 'loading' ? <WaitNote className="empty">Checking…</WaitNote> : null}
      {state.status === 'failed' ? <p className="empty">{state.message}</p> : null}

      {state.status === 'ready' && listed.length === 0 ? (
        <p className="empty">
          No hours here this week. Try the next one, or another place.
        </p>
      ) : null}

      {byDay.map(([day, slots]) => (
        <div key={day} className="day-group">
          <span className="section-label">{day}</span>
          {slots.map((slot) => (
            <Link
              key={slot.slot_id}
              className="hour-row"
              to={slot.clinic_id ? `/booking/clinics/${slot.clinic_id}` : `/booking/${slot.slot_id}`}
            >
              <span className="hour-row-text">
                <span className="name">{slot.clinic_title ?? startTime(slot.slot_start)}</span>
                <span className="detail">
                  {slot.clinic_id ? `${clinicTimes(slot.slot_start, slot.slot_end)} · ` : ''}Coach {slot.coach_name}
                </span>
              </span>
              {slot.seats_free === null ? null : (
                <span className={slot.clinic_id && slot.seats_free <= 0 ? 'places clinic-full' : 'places'}>
                  {placesLabel(slot.seats_free)}
                </span>
              )}
              <span className="chevron" aria-hidden="true">
                ›
              </span>
            </Link>
          ))}
        </div>
      ))}

      {showCart && cart && cart.lines.length > 0 ? (
        <CartCta to="/cart" label="Go to cart" expiresAt={cart.expires_at} />
      ) : null}
    </>
  );
}

function remembered(): string | null {
  try {
    return window.localStorage.getItem(REMEMBERED_FACILITY);
  } catch {
    return null;
  }
}

/**
 * Monday to Monday, in the viewer's own week.
 *
 * The calendar is read where the person is, so the window is built from their
 * clock. Which hours exist inside it is the server's answer, computed in the
 * facility's timezone, and that is the one that decides what is bookable.
 */
function weekWindow(offset: number): { from: Date; to: Date } {
  const now = new Date();
  const monday = mondayOf(now);
  monday.setDate(monday.getDate() + offset * 7);

  const next = new Date(monday);
  next.setDate(monday.getDate() + 7);

  // This week starts now, not last Monday: an hour that has passed cannot be
  // booked and showing it is a row that refuses when tapped.
  return { from: offset === 0 && monday < now ? now : monday, to: next };
}

function mondayOf(day: Date): Date {
  const monday = new Date(day);
  monday.setDate(day.getDate() - ((day.getDay() + 6) % 7));
  monday.setHours(0, 0, 0, 0);
  return monday;
}

function lookAheadWindow(): { from: Date; to: Date } {
  const from = new Date();
  const to = new Date(from);
  to.setDate(from.getDate() + LOOK_AHEAD_DAYS);
  // setDate crosses a clock change as a calendar day, which can be an hour over
  // the server's limit counted in hours.
  return { from, to: new Date(Math.min(to.getTime(), from.getTime() + LOOK_AHEAD_DAYS * DAY_MS)) };
}

/**
 * Whole weeks from this one to the earliest hour. Nothing open, or the question
 * failing, leaves the calendar on this week, which says so for itself.
 */
function firstOpenWeek(ahead: SlotsState): number {
  if (ahead.status !== 'ready' || ahead.slots.length === 0) return 0;

  const earliest = Math.min(...ahead.slots.map((slot) => new Date(slot.slot_start).getTime()));
  const weeks = (mondayOf(new Date(earliest)).getTime() - mondayOf(new Date()).getTime()) / WEEK_MS;
  // Round: a clock change makes the gap an hour off a whole number of weeks.
  return Math.round(weeks);
}

function weekLabel(from: Date, to: Date): string {
  const last = new Date(to.getTime() - 1);
  const month = { month: 'short' } as const;
  const same = from.getMonth() === last.getMonth();
  return same
    ? `${from.getDate()} – ${last.getDate()} ${last.toLocaleDateString('en-GB', month)}`
    : `${from.getDate()} ${from.toLocaleDateString('en-GB', month)} – ${last.getDate()} ${last.toLocaleDateString('en-GB', month)}`;
}

function startTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit' });
}

function groupByDay(slots: Slot[]): [string, Slot[]][] {
  const days = new Map<string, Slot[]>();

  for (const slot of slots) {
    const day = new Date(slot.slot_start).toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
    days.set(day, [...(days.get(day) ?? []), slot]);
  }

  return [...days.entries()];
}
